<template>
  <div class="fixed inset-0 flex flex-col bg-gray-900" style="z-index: 55;">

    <!-- Progress + back -->
    <div class="pt-6 pb-2 flex items-center justify-center gap-3">
      <button
        @click="$emit('go-back')"
        class="text-gray-500 hover:text-gray-300 transition-colors"
      >
        <unicon name="arrow-left" class="w-4 h-4" fill="currentColor" />
      </button>
      <div class="text-sm uppercase tracking-widest font-bold text-gray-500">
        {{ isEdit ? 'Edit item' : `${itemIndex + 1} of ${itemTotal}` }}
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 flex flex-col items-center justify-center px-8 text-center">

      <!-- Unexpected item banner -->
      <div v-if="isExtra" class="mb-6 w-full max-w-sm px-4 py-3 rounded-lg border border-amber-500 border-opacity-50 bg-amber-500 bg-opacity-10">
        <div class="text-xs uppercase tracking-widest font-bold text-amber-400 mb-1">Unexpected item</div>
        <div class="text-sm text-amber-300">Wasn't on the PO — decide whether to accept</div>
      </div>

      <div class="text-4xl sm:text-5xl font-bold text-white leading-tight mb-1">
        {{ productName }}
      </div>
      <div class="text-sm font-mono text-gray-500 mb-8">
        <div v-if="vendorProductName">Vendor Product: {{ vendorProductName }}</div>
        <div v-if="productCode">Product Code: {{ productCode }}</div>
        <div v-if="!vendorProductName && !productCode">&nbsp;</div>
      </div>

      <!-- Expected qty — large focal point -->
      <div v-if="!isExtra" class="text-center mb-6">
        <div class="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2">Expected</div>
        <div class="text-7xl sm:text-8xl font-bold text-white leading-none">{{ expectedQty !== false ? expectedQty : 0 }}</div>
      </div>

      <!-- Pack size — secondary -->
      <div v-if="!isExtra" class="text-center">
        <div class="text-xs uppercase tracking-wide text-gray-500 font-bold">Pack Size</div>
        <div class="text-xl font-semibold text-gray-300 mt-1">{{ packSize }}</div>
      </div>

      <!-- Extra item invoiced qty -->
      <div v-if="isExtra && scanQuantity !== null" class="text-center">
        <div class="text-xs uppercase tracking-wide text-gray-500 font-bold">Invoiced Qty</div>
        <div class="text-7xl sm:text-8xl font-bold text-teal-400 leading-none mt-2">{{ scanQuantity }}</div>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex border-t border-gray-700 flex-shrink-0">
      <button
        @click="$emit('reject', productId)"
        class="flex-1 py-10 flex items-center justify-center hover:bg-red-900 hover:bg-opacity-20 transition-colors border-r border-gray-700"
      >
        <unicon name="times" class="w-10 h-10" fill="#f87171" />
      </button>
      <button
        @click="handleAccept"
        class="flex-1 py-10 flex items-center justify-center hover:bg-green-900 hover:bg-opacity-20 transition-colors"
      >
        <unicon name="check" class="w-10 h-10" fill="#4ade80" />
      </button>
    </div>

    <!-- Put Away modal -->
    <div
      v-if="showPutAwayModal"
      class="fixed inset-0 flex items-center justify-center p-6 bg-gray-900 bg-opacity-80"
      style="z-index: 60"
    >
      <div class="w-full max-w-sm rounded-2xl bg-gray-700 overflow-hidden shadow-xl text-center">
        <div class="px-8 pt-8 pb-6">
          <div class="text-xs uppercase tracking-widest font-bold text-gray-400 mb-3">Put Away</div>
          <div class="text-4xl font-bold text-white leading-tight capitalize">{{ putAwayLocation }}</div>
        </div>
        <div class="border-t border-gray-600">
          <button
            @click="confirmAccept"
            class="w-full py-5 text-white text-lg font-semibold hover:bg-gray-600 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'ReceivingItemCard',
  props: {
    productId: { type: String, required: true },
    invoiceId: { type: String, required: true },
    itemIndex: { type: Number, required: true },
    itemTotal: { type: Number, required: true },
    scanQuantity: { type: Number, default: null },
    isExtra: { type: Boolean, default: false },
    isEdit: { type: Boolean, default: false }
  },
  data() {
    return {
      showPutAwayModal: false
    }
  },
  computed: {
    ...mapGetters('storeModule/products', ['productById']),
    ...mapGetters('storeModule/invoices', ['invoiceById']),
    ...mapGetters('vendorProducts', ['vendorProductCodeIndex']),

    product() {
      return this.productById(this.productId) || {}
    },

    productName() {
      return this.product.name || this.productId
    },

    productCode() {
      return this.product.code || null
    },

    vendorProductName() {
      const { vendor, code } = this.product
      if (!vendor || !code) return null
      const vp = this.vendorProductCodeIndex[`${vendor}|${String(code).toLowerCase()}`]
      return vp ? vp.name : null
    },

    packSize() {
      if (this.product.unit) return this.product.unit
      if (this.product.vendorPackSize) return this.product.vendorPackSize
      if (this.product.qtyPerUnit > 1) return `${this.product.qtyPerUnit} ct`
      return '—'
    },

    putAwayLocation() {
      return this.product.location || null
    },

    expectedQty() {
      const inv = this.invoiceById(this.invoiceId)
      if (!inv || !inv[this.productId]) return false
      return inv[this.productId].amountOrdered
    }
  },
  methods: {
    handleAccept() {
      if (this.putAwayLocation && !this.isExtra) {
        this.showPutAwayModal = true
      } else {
        this.$emit('accept', this.productId)
      }
    },
    confirmAccept() {
      this.showPutAwayModal = false
      this.$emit('accept', this.productId)
    }
  }
}
</script>
